import React from "react";
import useSavedComponentsState from "../use-saved-components-state";
import axios from "axios";
import useIsMounted from "../use-is-mounted";
import BroadcastService from "../../../core/services/broadcast";

export interface LayoutSettingsCachedData {
    isLayoutCompact?: boolean;
    themeMode?: 'light' | 'dark' | 'system';
    locale?: string;
}

export type CachedDataKeys = (keyof LayoutSettingsCachedData)[];

export const usePanelLayoutConfiguration = ({fetch = true} = {}) => {
    const [submitting, setSubmitting] = React.useState<CachedDataKeys>([]);
    const [cachedLayoutSettings, _setCachedLayoutSettings] = React.useState<LayoutSettingsCachedData>();
    const isMounted = useIsMounted();
    const {highestVersion: getCachedLayoutSettings, save: saveCachedLayoutSettings} = useSavedComponentsState({
        name: `layout-settings`,
        version: '1.0.0'
    });

    /**
     * Sets the cached layout settings and also saves them in the cache.
     */
    const setCachedLayoutSettings = React.useCallback(async (updater, commit = true, properties: CachedDataKeys = []) => {
        const newState = await new Promise((resolve) => {
            _setCachedLayoutSettings((prevState) => {
                const newState = typeof updater === 'function' ? updater(prevState) : updater;
                resolve(newState);
                return newState;
            });
        });
        if (commit) {
            BroadcastService.postMessage(BroadcastService.channelNames.PanelLayoutSettings, newState);
            setSubmitting(prevState => [...prevState, ...properties]);
            void saveCachedLayoutSettings(prevState => ({
                ...(prevState ?? {}),
                ...(newState ?? {}),
            })).finally(() => {
                setSubmitting(prevState => prevState.filter(e => properties?.includes(e)));
            });
        }
    }, [saveCachedLayoutSettings])

    /**
     * Listens to changes in the Personal settings from other tabs/windows and updates the cached settings accordingly.
     */
    React.useEffect(() => {
        const {close} = BroadcastService.addEventListener(BroadcastService.channelNames.PanelLayoutSettings, 'message', (message) => {
            void setCachedLayoutSettings(message, false);
        });
        return () => {
            close?.();
        }
    }, [setCachedLayoutSettings]);

    /**
     * Fetches the cached layout settings.
     */
    const fetchCachedLayoutSettings = React.useCallback(async (cancelToken) => {
        const cachedSettings = await getCachedLayoutSettings(cancelToken);
        if (!isMounted()) {
            return;
        }
        void setCachedLayoutSettings(cachedSettings?.data, false);
    }, [getCachedLayoutSettings, isMounted, setCachedLayoutSettings]);

    /**
     * As soon as the component mounts:
     * - fetches the cached layout settings.
     */
    React.useEffect(() => {
        if (!fetch)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void fetchCachedLayoutSettings(cancelTokenSource.token);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [fetchCachedLayoutSettings, fetch])


    const isLayoutCompact = React.useMemo(() => cachedLayoutSettings?.isLayoutCompact ?? false, [cachedLayoutSettings]);

    /**
     * Sets whether the layout is in compact mode.
     * @type {(function(*): void)}
     */
    const setIsLayoutCompact = React.useCallback<React.Dispatch<React.SetStateAction<LayoutSettingsCachedData['isLayoutCompact']>>>((updater) => {
        void setCachedLayoutSettings((prev) => {
            const newIsPanelInCompactMode = typeof updater === 'function' ? updater(prev?.isLayoutCompact ?? false) : updater;
            return {
                ...(prev ?? {}),
                isLayoutCompact: newIsPanelInCompactMode,
            }
        }, true, ['isLayoutCompact']);
    }, [setCachedLayoutSettings]);

    const themeMode = React.useMemo(() => cachedLayoutSettings?.themeMode ?? 'light', [cachedLayoutSettings]);

    /**
     * Sets the theme mode.
     * @type {(function(*): void)}
     */
    const setThemeMode = React.useCallback<React.Dispatch<React.SetStateAction<LayoutSettingsCachedData['themeMode']>>>((updater) => {
        void setCachedLayoutSettings((prev) => {
            const newThemeMode = typeof updater === 'function' ? updater(prev?.themeMode ?? 'light') : updater;
            return {
                ...(prev ?? {}),
                themeMode: newThemeMode,
            }
        }, true, ['themeMode']);
    }, [setCachedLayoutSettings]);

    const locale = React.useMemo(() => cachedLayoutSettings?.locale ?? 'en', [cachedLayoutSettings]);

    /**
     * Sets the locale.
     * @type {(function(*): void)}
     */
    const setLocale = React.useCallback<React.Dispatch<React.SetStateAction<LayoutSettingsCachedData['locale']>>>((updater) => {
        void setCachedLayoutSettings((prev) => {
            const newLocale = typeof updater === 'function' ? updater(prev?.locale ?? 'en') : updater;
            return {
                ...(prev ?? {}),
                locale: newLocale,
            }
        }, true, ['locale']);
    }, [setCachedLayoutSettings]);

    return ({
        submitting,
        isLayoutCompact,
        setIsLayoutCompact,
        themeMode,
        setThemeMode,
        locale,
        setLocale,
    })
}